import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  usePublicBookingByToken,
  useCancelPublicBooking,
} from '@hooks/usePublicBooking';
import { BookingStatusBadge } from '@components/common/BookingStatusBadge';
import { ConfirmDialog } from '@components/common/ConfirmDialog';
import { PageLoading } from '@components/common/LoadingSpinner';
import { Button } from '@components/ui/button';
import { formatDateTime } from '@lib/utils';
import { ApiRequestError } from '@api/client';
import { useToast } from '@hooks/useToast';
import { CustomerReschedule } from './CustomerReschedule';

export function CustomerPortal() {
  const { trackingToken } = useParams<{ trackingToken: string }>();
  const { t } = useTranslation();
  const { toast } = useToast();

  const token = trackingToken ?? '';
  const { data: booking, isLoading, isError, error } = usePublicBookingByToken(token);
  const cancelMutation = useCancelPublicBooking(token);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  if (isLoading) return <PageLoading />;

  // Token invalid / expired
  if (isError) {
    const isTokenError =
      error instanceof ApiRequestError &&
      (error.status === 404 || error.status === 410);
    return (
      <main className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-sm text-center" data-testid="token-error">
          <h1 className="mb-2 text-xl font-bold text-destructive">
            {isTokenError ? t('booking.invalidToken') : t('errors.serverError')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('booking.expiredToken')}
          </p>
        </div>
      </main>
    );
  }

  if (!booking) return null;

  const canCancel = booking.status === 'pending' || booking.status === 'confirmed';
  const canReschedule = booking.status === 'confirmed' || booking.status === 'rescheduled';

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync();
      setCancelled(true);
      toast({ title: t('booking.cancelSuccess') });
    } catch {
      toast({ title: t('errors.serverError'), variant: 'destructive' });
    } finally {
      setCancelOpen(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <div
        className="w-full max-w-md rounded-lg border bg-card p-6 shadow-sm"
        data-testid="customer-portal"
      >
        <h1 className="mb-6 text-xl font-bold">{t('booking.trackingTitle')}</h1>

        {cancelled && (
          <div role="status" className="mb-4 rounded-md bg-muted p-3 text-sm text-muted-foreground">
            {t('booking.cancelSuccess')}
          </div>
        )}

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
          <dt className="font-medium text-muted-foreground">{t('common.status')}</dt>
          <dd>
            <BookingStatusBadge status={booking.status} />
          </dd>

          <dt className="font-medium text-muted-foreground">{t('common.date')}</dt>
          <dd>{formatDateTime(booking.startTime, { timezone: booking.centerTimezone })}</dd>

          {booking.centerName && (
            <>
              <dt className="font-medium text-muted-foreground">Centro</dt>
              <dd>{booking.centerName}</dd>
            </>
          )}

          <dt className="font-medium text-muted-foreground">Servicio</dt>
          <dd>{booking.serviceName}</dd>

          {booking.staffName && (
            <>
              <dt className="font-medium text-muted-foreground">Profesional</dt>
              <dd>{booking.staffName}</dd>
            </>
          )}

          {booking.roomName && (
            <>
              <dt className="font-medium text-muted-foreground">Sala</dt>
              <dd>{booking.roomName}</dd>
            </>
          )}
        </dl>

        {!cancelled && (
          <div className="mt-6 flex flex-col gap-3">
            {canReschedule && (
              <Button
                variant="outline"
                onClick={() => { setRescheduleOpen(true); }}
                data-testid="reschedule-button"
              >
                {t('booking.actions.reschedule')}
              </Button>
            )}
            {canCancel && (
              <Button
                variant="destructive"
                onClick={() => { setCancelOpen(true); }}
                data-testid="cancel-booking-button"
              >
                {t('booking.actions.cancel')}
              </Button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t('booking.actions.cancel')}
        description={t('booking.cancelConfirm')}
        onConfirm={() => void handleCancel()}
        isLoading={cancelMutation.isPending}
      />

      {rescheduleOpen && booking.centerId && (
        <CustomerReschedule
          open={rescheduleOpen}
          onOpenChange={setRescheduleOpen}
          trackingToken={token}
          centerSlug={booking.centerId}
          centerTimezone={booking.centerTimezone}
          serviceId={booking.serviceId}
          staffId={booking.staffId ?? undefined}
        />
      )}
    </main>
  );
}
